import React, { useMemo, useState } from 'react';

import { defineMessage, FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { MarkdownTransformer } from '@atlaskit/editor-markdown-transformer';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { ADFEncoder, ReactRenderer } from '@atlaskit/renderer';
import { token } from '@atlaskit/tokens';
import { AgentActionPill } from '@atlassian/conversation-assistant-agent/ui/AgentActionPill';
import { InlineIconText } from '@atlassian/conversation-assistant-agent/ui/InlineIconText';
import {
	type Knowledge,
	KnowledgePillList,
} from '@atlassian/conversation-assistant-agent/ui/KnowledgePillList';

/**
 * Shape consumed by the inline tool pill list. Mirrors the canonical FE
 * `AgentScenarioAction` shape that `AgentActionPill` accepts.
 */
export type Tool = {
	id: string;
	displayName: string;
	iconUrl?: string;
	tags?: string[];
	isActive?: boolean;
};

// Module-scoped so we only build the schema/transformer once per app session.
const adfEncoder = new ADFEncoder((schema) => new MarkdownTransformer(schema));

const Markdown = ({ source }: { source: string }) => (
	<ReactRenderer document={adfEncoder.encode(source)} />
);

const descriptionLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.ExpandableBody.descriptionLabel',
	defaultMessage: 'Description',
	description: 'Section heading for the agent description in the expanded card body.',
});

const instructionsLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.ExpandableBody.instructionsLabel',
	defaultMessage: 'Instructions',
	description: 'Section heading for the agent instructions in the expanded card body.',
});

const knowledgeLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.ExpandableBody.knowledgeLabel',
	defaultMessage: 'Knowledge',
	description: 'Section heading for the agent knowledge sources in the expanded card body.',
});

const toolsLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.ExpandableBody.toolsLabel',
	defaultMessage: 'Tools',
	description: 'Section heading for the agent tool pills in the expanded card body.',
});

const expandLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.ExpandableBody.expandLabel',
	defaultMessage: 'Show more',
	description: 'Accessible label for the chevron-down button that expands the card body.',
});

const showMoreToolsLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.ExpandableBody.showMoreToolsLabel',
	defaultMessage: 'Show more',
	description: 'Button label that reveals the remaining tool pills beyond the initial 5.',
});

const showMoreToolsAriaLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.ExpandableBody.showMoreToolsAriaLabel',
	defaultMessage: 'Show more tools',
	description:
		'Accessible label for the "Show more" button in the tools list. Disambiguates from the card-level "Show more" expand button so screen readers and automated tests can distinguish between the two.',
});

const MAX_VISIBLE_TOOLS = 5;

const CLOSED_BODY_MAX_HEIGHT = 145;
const FADE_HEIGHT = 60;

const styles = cssMap({
	wrapper: {
		position: 'relative',
	},
	bodyClosed: {
		maxHeight: `${CLOSED_BODY_MAX_HEIGHT}px`,
		overflow: 'hidden',
	},
	fade: {
		position: 'absolute',
		insetInline: '0',
		insetBlockEnd: '0',
		height: `${FADE_HEIGHT}px`,
		backgroundImage: `linear-gradient(to bottom, transparent, ${token('elevation.surface')})`,
		pointerEvents: 'none',
	},
	floatingChevron: {
		position: 'absolute',
		insetInlineStart: '50%',
		insetBlockEnd: token('space.100'),
		transform: 'translateX(-50%)',
		pointerEvents: 'auto',
		zIndex: 1,
		borderRadius: token('radius.full'),
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('elevation.surface.overlay'),
	},
	sectionLabel: {
		font: token('font.heading.xxsmall'),
		color: token('color.text.subtlest'),
	},
});

const Section = ({ label, children }: { label: MessageDescriptor; children: React.ReactNode }) => (
	<Stack space="space.075">
		<Box xcss={styles.sectionLabel}>
			<FormattedMessage {...label} />
		</Box>
		<Box>{children}</Box>
	</Stack>
);

const ToolsList = ({ tools }: { tools: Tool[] }) => {
	const intl = useIntl();
	const [showAll, setShowAll] = useState(false);
	const visibleTools = showAll ? tools : tools.slice(0, MAX_VISIBLE_TOOLS);
	const overflow = tools.length - MAX_VISIBLE_TOOLS;
	const hasMore = overflow > 0;

	return (
		<Stack space="space.075" alignInline="start">
			<Flex rowGap="space.050" columnGap="space.075" wrap="wrap" alignItems="center">
				{visibleTools.map((tool, index) => (
					<Box key={`tool-pill-${tool.id}-${index}`}>
						<AgentActionPill
							action={{
								id: tool.id,
								displayName: tool.displayName,
								tags: tool.tags,
								iconUrl: tool.iconUrl,
							}}
							isActive={tool.isActive ?? true}
						/>
					</Box>
				))}
				{!showAll && hasMore && <InlineIconText content={`+ ${overflow}`} fullWidth />}
			</Flex>
			{hasMore && !showAll && (
				<Button
					appearance="subtle"
					spacing="compact"
					aria-label={intl.formatMessage(showMoreToolsAriaLabel)}
					onClick={() => setShowAll(true)}
				>
					<FormattedMessage {...showMoreToolsLabel} />
				</Button>
			)}
		</Stack>
	);
};

export type ExpandableBodyProps = {
	cloudId?: string;
	description: string;
	instructions: string;
	knowledge: Knowledge[];
	tools: Tool[];
	isExpanded: boolean;
	onExpand: () => void;
};

export const ExpandableBody = ({
	cloudId,
	description,
	instructions,
	knowledge,
	tools,
	isExpanded,
	onExpand,
}: ExpandableBodyProps): React.JSX.Element => {
	const intl = useIntl();

	const hasVisibleKnowledge = useMemo(
		() => knowledge.some((edge) => edge.enabled && edge.sources?.some((source) => source?.enabled)),
		[knowledge],
	);

	return (
		<Box xcss={styles.wrapper}>
			<Box xcss={isExpanded ? undefined : styles.bodyClosed}>
				<Stack space="space.200">
					{description && (
						<Section label={descriptionLabel}>
							<Markdown source={description} />
						</Section>
					)}
					{instructions && (
						<Section label={instructionsLabel}>
							<Markdown source={instructions} />
						</Section>
					)}
					{hasVisibleKnowledge && cloudId && (
						<Section label={knowledgeLabel}>
							<KnowledgePillList
								cloudId={cloudId}
								knowledge={knowledge}
								preferDirectConnectorFetch
							/>
						</Section>
					)}
					{tools.length > 0 && (
						<Section label={toolsLabel}>
							<ToolsList tools={tools} />
						</Section>
					)}
				</Stack>
			</Box>
			{!isExpanded && (
				<>
					<Box xcss={styles.fade} />
					<Box xcss={styles.floatingChevron}>
						<IconButton
							testId="rovo-create-agent-card-expand"
							appearance="default"
							shape="circle"
							isTooltipDisabled={false}
							icon={ChevronDownIcon}
							label={intl.formatMessage(expandLabel)}
							onClick={onExpand}
						/>
					</Box>
				</>
			)}
		</Box>
	);
};
